import { Shape, ShapeType } from '@/services/shape';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  insertImages
} from '../utils/resource.utils';
import { shapesToLayoutAst } from '.';
import { encodeHTMLEntities, getRefName } from '../utils/name.utils';
import {
  addWidgetTranslationForApp,
  addWidgetTranslationIfNeed
} from '../utils/translation.utils';
import { buildAstState } from '..';
import { getActuallyWidgetSize } from '@/utils/shape/widget.size.utils';
import { jumpSportList } from '@/resources/jump-sport-list';
import {
  WidgetTranslationKey,
  widgetTranslations
} from '@/resources/widget-translations';
import { JumpSportTranslationKey } from '@/resources/jump-sport-translations';
import { jumpSportListO61 } from '@/resources/jump-sport-list-O61';
import { getConditionAst } from './condition';

/**
 * 根据 widgetName（图层名称）反查 widget-translations 中的 key
 * 用于老表盘未设置 relatedDataKey 时的兜底
 */
const _reverseLoopupDataKey = (widgetName: string): string | undefined => {
  if (!widgetName) return undefined;
  const zhCN = widgetTranslations['zh_CN'];
  for (const [key, value] of Object.entries(zhCN)) {
    if (value === widgetName) return key;
  }
  return undefined;
};

/**
 * 返回类型为slot widget group的ast数据
 * @param resourceAst
 * @param shape
 * @returns
 */
export const getWidgetAst = (
  resourceAst: Ast,
  shape: Shape,
  layer: StoreLayerItem,
  parent: StoreLayerItem | undefined
) => {
  const {
    name,
    w = 0,
    h = 0,
    x = 0,
    y = 0,
    jumpApp = 'none',
    resources,
    flexDirection = 'disabled',
    gap = 0,
    justifyContent = 'flex-start',
    alignItems = 'flex-start',
    relatedDataKey,
    jumpAppSportId,
    jumpAppCustomName,
    jumpAppAlipayContent
  } = shape.attrs;

  const deviceType = buildAstState.data.description?.deviceType;

  const ast: Ast = {
    tag: 'Widget',
    attrsMap: {
      // 当关联数据项后需要使用数据项名称-中文,
      // 否则使用图层名称
      widgetName: encodeHTMLEntities(name ?? ''),
      groupType: 'general'
    },
    children: []
  };

  const slotShape = Shape.getShape(parent?.id ?? '', parent);
  if (slotShape?.type == ShapeType.Slot) {
    // 添加editbox属性
    const imageMap = insertImages(
      resourceAst,
      {
        editBox: slotShape.attrs.sameEditBox
          ? slotShape.attrs.resources?.editBox
          : resources?.editBox,
        widgetPreview: resources?.widgetPreview
      },
      {
        recolorEnable: true
      }
    );
    // 只有当 editBox 资源存在时才设置该属性，避免生成无效的 UID
    if (imageMap['editBox']) {
      ast.attrsMap['editBox'] = getRefName(imageMap['editBox']);
    }
    // 只有当 widgetPreview 资源存在时才设置该属性
    if (imageMap['widgetPreview']) {
      ast.attrsMap['preview'] = getRefName(imageMap['widgetPreview']);
    }
  }

  // 导出时，所有 Widget 都需要 w/h
  // 使用 getActuallyWidgetSize 重新计算子元素包围盒尺寸（解决数字填充图组位数变化导致宽度不准）
  // 同时取 shape.attrs.w/h 的较大值，避免跳转 Widget 的点击区域被缩小到子元素尺寸
  const actualSize = getActuallyWidgetSize(shape);
  const { w: aw = 0, h: ah = 0 } = actualSize ?? {};
  const w1 = Math.max(aw, w ?? 0) || 100;
  const h1 = Math.max(ah, h ?? 0) || 100;
  if (buildAstState.options.buildFor == 'export') {
    ast.attrsMap.w = String(w1);
    ast.attrsMap.h = String(h1);
  }

  // 添加跳转应用
  if (jumpApp && jumpApp != 'none') {
    // 如果是自定义名称，使用自定义名称作为jumpApp值
    if (jumpApp === 'customName' && jumpAppCustomName) {
      ast.attrsMap.jumpApp = jumpAppCustomName;
    } else {
      ast.attrsMap.jumpApp = jumpApp;
    }

    ast.attrsMap.w = String(w1);
    ast.attrsMap.h = String(h1);

    // 如果跳转应用为sport时，需要添加跳转类型
    if (jumpApp == 'sport' && (jumpAppSportId ?? -1) >= 0) {
      ast.attrsMap.args = String(jumpAppSportId);
    }

    // 电量是设置的二级菜单，所以appid相同，用args区分
    if (jumpApp == 'battery') {
      ast.attrsMap.jumpApp = 'settings';
      ast.attrsMap.args = 'battery';
    }

    // 支付宝支付内容，使用args传递（打开应用为默认行为，不需要args）
    if (jumpApp == 'alipay' && jumpAppAlipayContent && jumpAppAlipayContent !== 'openApp') {
      const alipayArgsMap: Record<string, string> = {
        paymentCode: 'paycode',
        tapPay: 'tapPay'
      };
      const argsVal = alipayArgsMap[jumpAppAlipayContent];
      if (argsVal) {
        ast.attrsMap.args = argsVal;
      }
    }
  }

  const layerChildren = [...(layer.children ?? [])];
  if (flexDirection == 'column' || flexDirection == 'row') {
    ast.attrsMap = {
      ...ast.attrsMap,
      w: String(w),
      h: String(h),
      flex_direction: flexDirection,
      justify_content: justifyContent,
      align_items: alignItems,
      align_content: alignItems,
      gap: String(gap)
    };
    // 当为自动布局时，生成的顺序需要倒序
    layerChildren.reverse();
  }

  // 多语言
  // 获取翻译key
  let translationKey:
    | WidgetTranslationKey
    | JumpSportTranslationKey
    | undefined;
  // 优先获取sport的key值
  // 如果跳转应用为sport时且关联数据项不是禁飞时间和水面间隔时间，则需要增加运动翻译
  if (
    jumpApp == 'sport' &&
    (jumpAppSportId ?? -1) >= 0 &&
    !['no_fly_time.sport', 'shuimianjiange.wf'].includes(relatedDataKey ?? '')
  ) {
    // 通过sportName获取翻译key
    translationKey = (deviceType == 'N62S' ? jumpSportList : jumpSportListO61)
      .map((item) => [...item.children])
      .flat()
      .find((item) => item.sportId === jumpAppSportId)?.translationKey;
  } else if (relatedDataKey && relatedDataKey != 'none') {
    if (relatedDataKey == 'none_translation') {
      // 增加 无 的翻译
      translationKey = 'none';
    } else {
      translationKey = relatedDataKey;
    }
  }
  if (translationKey) {
    addWidgetTranslationIfNeed(resourceAst, ast, translationKey);
  }

  // 写入 dataKey 供 app 端统一识别数据语义（不影响 BIN 编译）
  if (relatedDataKey) {
    ast.attrsMap.dataKey = relatedDataKey;
  } else {
    // 兜底：从 widgetName（图层名称）反查翻译表中的 key
    const fallbackKey = _reverseLoopupDataKey(name ?? '');
    ast.attrsMap.dataKey = fallbackKey ?? 'none';
  }

  // 此时widgetName可能仍为绑定多语言（显示数据项或运动名称），为支持app， 特意为app生成多语言
  if (
    buildAstState.options.exportForApp &&
    !ast.attrsMap.widgetName?.startsWith('@') &&
    parent?.type == ShapeType.Slot
  ) {
    addWidgetTranslationForApp(resourceAst, ast);
  }

  const itemAsts = shapesToLayoutAst(resourceAst, layerChildren, layer);
  // 去除xy属性
  if (flexDirection == 'column' || flexDirection == 'row') {
    itemAsts.forEach((item) => {
      delete item.attrsMap.x;
      delete item.attrsMap.y;
    });
  }

  if (shape.attrs.minClickZone || slotShape?.attrs.minClickZone) {
    ast.attrsMap.minClickZone = 'true';
  }

  // 重置tag（原tag为layout）
  itemAsts.forEach((item) => (item.tag = 'Item'));

  // 组的条件切换动效：将 widgetConditions 按顺序分配给子 Widget
  // 前 N-1 个子 Widget 绑定对应的 Condition，最后一个作为 else 默认显示
  const widgetConditions = shape.attrs.widgetConditions;
  if (
    shape.canUseWidgetCondition &&
    widgetConditions?.length &&
    itemAsts.length > 1
  ) {
    // 反转 itemAsts 以匹配 widgetConditions 的顺序（与结构树显示顺序一致）
    const reversedItems = [...itemAsts].reverse();

    if (buildAstState.options.buildFor === 'export') {
      // ===== 导出模式：生成 Slot + Condition 结构 =====
      const slotChildren: Ast[] = [];

      const getItemRef = (item: Ast) => {
        const refName = item.attrsMap.ref?.replace('@', '');
        const targetResource = resourceAst.children.find(
          (child) => child.attrsMap.name === refName
        );
        if (targetResource?.tag === 'Widget') {
          return item.attrsMap.ref;
        }
        // 非组子元素包裹 Widget，x/y 设为 0（与容器子元素一致）
        const wrappedWidget = addResourceIfNotExists(resourceAst, {
          tag: 'Widget',
          attrsMap: {
            name: encodeHTMLEntities('Widget')
          },
          children: [
            {
              tag: 'Item',
              attrsMap: {
                ref: item.attrsMap.ref,
                x: '0',
                y: '0',
                _id: item.attrsMap._id,
                _alpha: item.attrsMap._alpha,
                _scale: item.attrsMap._scale
              },
              children: []
            }
          ]
        });
        return `@${wrappedWidget.attrsMap.name}`;
      };

      for (let i = 0; i < widgetConditions.length && i < reversedItems.length - 1; i++) {
        const condition = widgetConditions[i];
        if (!condition?.children?.length) continue;
        const conditionAst = getConditionAst(resourceAst, condition);
        slotChildren.push({
          tag: 'Item',
          attrsMap: {
            _tag: 'Condition',
            _name: `widgetCondition-条件${i + 1}`,
            ref: getItemRef(reversedItems[i]),
            condition: `@${conditionAst.attrsMap.name}`,
            selectMethod: 'inOrder',
            _id: reversedItems[i].attrsMap._id,
            _alpha: reversedItems[i].attrsMap._alpha,
            _scale: reversedItems[i].attrsMap._scale
          },
          children: []
        });
      }

      const lastItem = reversedItems[reversedItems.length - 1];
      slotChildren.push({
        tag: 'Item',
        attrsMap: {
          _tag: 'Condition',
          _name: 'widgetCondition-默认(else)',
          ref: getItemRef(lastItem),
          _id: lastItem.attrsMap._id,
          _alpha: lastItem.attrsMap._alpha,
          _scale: lastItem.attrsMap._scale
        },
        children: []
      });

      const slotAst = addResourceIfNotExists(resourceAst, {
        tag: 'Slot',
        attrsMap: {
          name: encodeHTMLEntities('Slot'),
          type: 'condition',
          compressMethod: 'RLEReversed',
          recolorEnable: 'false',
          _as: 'slot-condition-widget'
        },
        children: slotChildren
      });

      // 导出时直接用 Slot 作为 layout 的 ref（动效组只会是最外层结构）
      const layoutAst: Ast = generateLayoutAst({
        ref: '@' + slotAst.attrsMap.name,
        x: x,
        y: y
      });
      return layoutAst;
    } else {
      // ===== 编辑器模式：不修改 AST 结构，由渲染层实时判断条件 =====
      ast.children = [...itemAsts];
    }
  } else {
    ast.children = [...itemAsts];
  }

  const _ast = addResourceIfNotExists(resourceAst, ast);
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + _ast.attrsMap.name,
    x: x,
    y: y
  });

  return layoutAst;
};
